/* eslint-disable */


import React from "react";
import { Plus, Store, User, Printer } from "lucide-react";
import { sortByName } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { DEFAULT_ALL_EMPS } from "./constants";

export default function DistributorFormDialog({ open, onOpenChange, form, setForm, editing, onSave, onOpenCreate, products = [] }) {
  const [priceCategoryFilter, setPriceCategoryFilter] = React.useState("");
  // Catégories distinctes présentes dans les produits actifs
  const categories = React.useMemo(() => {
    const set = new Set();
    for (const p of products) {
      if (p.archived) continue;
      if (p.category) set.add(p.category);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [products]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button onClick={onOpenCreate} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-distributor-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau dépôt
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E5E5E0]">
          <DialogTitle className="font-heading">
            {editing
              ? form.type === "CLIENT"
                ? "Modifier le client"
                : "Modifier le distributeur"
              : "Nouveau dépôt / client"}
          </DialogTitle>
          <DialogDescription>
            {form.type === "CLIENT"
              ? "Un client correspond à un point de vente sans distributeur automatique. Aucune grille d'emplacements n'est gérée — la marchandise est simplement transférée dans le réfrigérateur du client."
              : editing
              ? "Mettre à jour les informations du distributeur."
              : `Le distributeur sera créé avec les emplacements 10 à 58 (${DEFAULT_ALL_EMPS.length} emplacements × 4 barquettes = ${DEFAULT_ALL_EMPS.length * 4} barquettes). Vous pourrez en supprimer ensuite.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 px-6 overflow-y-auto flex-1">
          <div>
            <Label className="mb-1.5 block">Type *</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="dist-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AUTOMATIQUE">
                  <div className="flex items-center gap-2">
                    <Store className="w-4 h-4" /> Distributeur automatique (avec emplacements)
                  </div>
                </SelectItem>
                <SelectItem value="CLIENT">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" /> Client (réfrigérateur, sans grille)
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            {editing && form.type !== (editing.type || "AUTOMATIQUE") && (
              <p className="text-[11px] text-[#B45309] mt-1">
                {form.type === "CLIENT"
                  ? "Bascule vers Client : la grille d'emplacements sera supprimée à l'enregistrement."
                  : "Bascule vers Automatique : une grille d'emplacements 10–58 sera proposée à l'enregistrement."}
              </p>
            )}
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <Label className="mb-1.5 block">{form.type === "CLIENT" ? "Nom du client *" : "Nom du dépôt *"}</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={form.type === "CLIENT" ? "Ex: Restaurant La Tablée" : "Ex: Entrepôt principal Lyon"}
                data-testid="dist-name"
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Code</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder={form.type === "CLIENT" ? "CLI-01" : "LYO-01"}
                data-testid="dist-code"
              />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Responsable</Label>
            <Input
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              placeholder="Nom du responsable"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label className="mb-1.5 block">Téléphone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Adresse</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="mb-1.5 block">Code postal</Label>
              <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
            </div>
            <div>
              <Label className="mb-1.5 block">Ville</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div>
              <Label className="mb-1.5 block">Pays</Label>
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </div>
          </div>
          {form.type === "AUTOMATIQUE" && (
            <div>
              <Label className="mb-1.5 block flex items-center gap-1.5">
                <span>Mise en service</span>
                <span className="text-[11px] font-normal text-[#575A57]">(format JJ.MM.AAAA — remonte dans le formulaire Canton VS)</span>
              </Label>
              <Input
                value={form.installed_at || ""}
                onChange={(e) => setForm({ ...form, installed_at: e.target.value })}
                placeholder="15.03.2025"
                className="font-mono"
                data-testid="dist-installed-at"
              />
            </div>
          )}
          <div>
            <Label className="mb-1.5 block">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          {form.type === "CLIENT" && (
            <div className="pt-2 border-t border-[#F0F0EB]">
              <Label className="mb-1.5 block">Niveau tarifaire appliqué</Label>
              <Select
                value={form.price_tier || "direct"}
                onValueChange={(v) => setForm({ ...form, price_tier: v })}
              >
                <SelectTrigger data-testid="price-tier-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct">Vente directe (Prix 1) — défaut</SelectItem>
                  <SelectItem value="distributor">Distributeur B2B (Prix 2)</SelectItem>
                  <SelectItem value="online">Internet (Prix 3)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#A3A6A3] mt-1">
                Détermine quel prix est appliqué automatiquement quand vous créez une commande, un bon de livraison ou
                enregistrez une vente pour ce client.
              </p>
              {/* Phase 101bn — Type de vente par défaut (Oseo = revente, restos = fabrication, etc.) */}
              <div className="mt-4">
                <Label className="mb-1.5 block">Type de vente par défaut</Label>
                <Select
                  value={form.default_sale_type || "fabrication"}
                  onValueChange={(v) => setForm({ ...form, default_sale_type: v })}
                >
                  <SelectTrigger data-testid="default-sale-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fabrication">🍳 Fabrication (OF classique — défaut)</SelectItem>
                    <SelectItem value="resale">🔄 Revente stock (LIFO auto, sans OF)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#A3A6A3] mt-1">
                  <b>Fabrication</b> : les commandes déclenchent la production d&apos;un OF dédié. <b>Revente</b> : les
                  commandes puisent directement dans le stock existant (LIFO ±2j), sans OF, avec BL immédiat.
                  Ce défaut peut être modifié par commande.
                </p>
              </div>
              {/* Phase 101fa (Feb 2026) — Laboratoire d'analyse HACCP (ARQHA & Co.) */}
              <div className="mt-4 pt-4 border-t border-[#F0F0EB]">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.is_laboratory}
                    onChange={(e) => setForm({ ...form, is_laboratory: e.target.checked })}
                    className="mt-1"
                    data-testid="dist-is-laboratory-toggle"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#1A1C1A]">🔬 Laboratoire d&apos;analyse HACCP</div>
                    <p className="text-[11px] text-[#575A57] mt-0.5">
                      Marque ce contact comme un laboratoire (ARQHA, autre). Chaque BL destiné
                      à ce labo génère automatiquement <strong>1 test HACCP en attente par produit + lot</strong>,
                      et son PDF affiche <strong>une ligne par lot avec le n° visible</strong>.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}
          {form.type === "AUTOMATIQUE" && (
            <div className="pt-2 border-t border-[#F0F0EB]">
              {/* Phase 101ge — Site interne à exclure des déclarations canton */}
              <label className="flex items-start gap-2 cursor-pointer mb-4 pb-4 border-b border-[#F0F0EB]">
                <input
                  type="checkbox"
                  checked={!!form.is_internal_stock}
                  onChange={(e) => setForm({ ...form, is_internal_stock: e.target.checked })}
                  className="mt-1"
                  data-testid="dist-is-internal-stock-toggle"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#1A1C1A]">📦 Site interne (stock, entrepôt, atelier)</div>
                  <p className="text-[11px] text-[#575A57] mt-0.5">
                    Ce site ne reçoit pas de clients publics. Il sera <strong>exclu du formulaire Police du Commerce</strong>
                    et du SCAV (économie 170.- par appareil non déclaré).
                  </p>
                </div>
              </label>
              {/* Phase 101gf — Machine « repas employés » (prix masqués site public) */}
              <label className="flex items-start gap-2 cursor-pointer mb-4 pb-4 border-b border-[#F0F0EB]">
                <input
                  type="checkbox"
                  checked={!!form.is_employees_only}
                  onChange={(e) => setForm({ ...form, is_employees_only: e.target.checked })}
                  className="mt-1"
                  data-testid="dist-is-employees-only-toggle"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#1A1C1A]">👷 Machine « repas employés » (ex: Novelis)</div>
                  <p className="text-[11px] text-[#575A57] mt-0.5">
                    Machine visible sur le site public pour permettre aux employés de voir les produits dispo,
                    mais les <strong>prix seront masqués côté site</strong> (repas offert).
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer mb-4 pb-4 border-b border-[#F0F0EB]">
                <input
                  type="checkbox"
                  checked={!!form.hidden_from_public}
                  onChange={(e) => setForm({ ...form, hidden_from_public: e.target.checked })}
                  className="mt-1"
                  data-testid="dist-hidden-from-public-toggle"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#1A1C1A]">Hors service temporaire : masquer du site public</div>
                  <p className="text-[11px] text-[#575A57] mt-0.5">
                    La machine disparaît du site maison-thai.pro (travaux, panne…). Décochez pour la remettre en ligne.
                  </p>
                </div>
              </label>
              <Label className="mb-1.5 block">ID Machine Nayax (optionnel)</Label>
              <Input
                value={form.nayax_machine_id}
                onChange={(e) => setForm({ ...form, nayax_machine_id: e.target.value.trim() })}
                placeholder="Ex: 12345678 (visible sur portail Nayax)"
                data-testid="nayax-machine-id"
              />
              <p className="text-[11px] text-[#A3A6A3] mt-1">
                Nécessaire pour synchroniser les ventes Nayax automatiquement. Configurez d'abord{" "}
                <code>NAYAX_API_KEY</code> dans les variables d'environnement.
              </p>
            </div>
          )}
          {form.type === "AUTOMATIQUE" && (
            <div className="pt-3 border-t border-[#F0F0EB] rounded-md">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="is-corporate"
                  checked={!!form.is_corporate}
                  onCheckedChange={(v) => setForm({ ...form, is_corporate: !!v })}
                  data-testid="is-corporate-toggle"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Label htmlFor="is-corporate" className="cursor-pointer font-medium flex items-center gap-2">
                    Machine interne Corporate
                    <span className="text-[10px] uppercase tracking-wider bg-[#F1EAF8] text-[#5B3A8C] px-1.5 py-0.5 rounded">
                      Novelis-like
                    </span>
                  </Label>
                  <p className="text-xs text-[#575A57] mt-0.5">
                    Machine installée chez une entreprise hôte qui subventionne les barquettes pour ses employés
                    et garantit un chiffre d'affaires mensuel minimum (delta facturable si non atteint).
                  </p>
                  <ul className="text-[11px] text-[#575A57] mt-1.5 pl-4 list-disc space-y-0.5">
                    <li><strong>Masquée</strong> du site public / API vitrine (visible uniquement en interne)</li>
                    <li>Alertes <em>« prix Nayax ≠ »</em> désactivées (prix subventionnés = légitimes)</li>
                    <li>Badge <span className="inline-block px-1 rounded bg-[#F1EAF8] text-[#5B3A8C]">Corporate</span> affiché dans Vitrine & liste distributeurs</li>
                  </ul>
                </div>
              </div>
              {form.is_corporate && (
                <div className="mt-4 space-y-4 pl-7" data-testid="corporate-fields">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="mb-1.5 block text-xs">Remise employé (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={form.employee_discount_percent ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm({
                            ...form,
                            employee_discount_percent: v === "" ? null : Number(v),
                          });
                        }}
                        placeholder="Ex: 30"
                        data-testid="employee-discount-pct"
                      />
                      <p className="text-[10px] text-[#A3A6A3] mt-1">
                        Indicatif uniquement — prix négociés gérés par direction.
                      </p>
                    </div>
                    <div>
                      <Label className="mb-1.5 block text-xs">CA mensuel garanti (CHF)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="50"
                        value={form.corporate_monthly_min_chf ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm({
                            ...form,
                            corporate_monthly_min_chf: v === "" ? null : Number(v),
                          });
                        }}
                        placeholder="Ex: 2000"
                        data-testid="corporate-monthly-min"
                      />
                      <p className="text-[10px] text-[#A3A6A3] mt-1">
                        Le delta sera facturé à l'entreprise hôte si non atteint.
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-xs">Seuil de report du CA excédentaire (CHF) — convention particulière</Label>
                    <Input
                      type="number"
                      min="0"
                      step="50"
                      value={form.corporate_carryover_threshold_chf ?? ""}
                      onChange={(e) => setForm({ ...form, corporate_carryover_threshold_chf: e.target.value === "" ? null : Number(e.target.value) })}
                      placeholder="Ex: 2200 (vide = pas de report)"
                      data-testid="corporate-carryover-threshold"
                    />
                    <p className="text-[10px] text-[#A3A6A3] mt-1">
                      Le CA au-delà de ce seuil est reporté sur le mois suivant, de mois en mois, sans limite. Un mois sous le seuil consomme tout le report.
                    </p>
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-xs">Email comptabilité de l'entreprise hôte</Label>
                    <Input
                      type="email"
                      value={form.corporate_billing_email || ""}
                      onChange={(e) =>
                        setForm({ ...form, corporate_billing_email: e.target.value.trim() })
                      }
                      placeholder="compta@novelis.example"
                      data-testid="corporate-billing-email"
                    />
                  </div>

                  {/* Phase 101et (Feb 2026) — Protection prix Corporate via magic link */}
                  <div className="pt-3 border-t border-[#F0E4C9] space-y-3">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!form.require_magic_link}
                        onChange={(e) => setForm({ ...form, require_magic_link: e.target.checked })}
                        className="mt-0.5 accent-[#5B3A8C]"
                        data-testid="corporate-require-magic-link"
                      />
                      <div>
                        <span className="text-sm font-semibold text-[#5B3A8C]">
                          Protéger l'accès par email pro (magic link)
                        </span>
                        <p className="text-[10px] text-[#575A57] mt-0.5 leading-snug">
                          L'employé devra saisir son email pro et cliquer sur un lien magique
                          reçu par email (valable 24h) pour voir les prix négociés. Évite le
                          partage sauvage du lien Corporate.
                        </p>
                      </div>
                    </label>
                    {form.require_magic_link && (
                      <div>
                        <Label className="mb-1.5 block text-xs">
                          Domaines email autorisés (un par ligne, sans @)
                        </Label>
                        <textarea
                          value={(form.allowed_email_domains || []).join("\n")}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              allowed_email_domains: e.target.value
                                .split(/[\n,]/)
                                .map((s) => s.trim().toLowerCase().replace(/^@/, ""))
                                .filter(Boolean),
                            })
                          }
                          placeholder="novelis.ch&#10;novelis.com"
                          rows={2}
                          className="w-full text-xs font-mono border border-[#E5E5E0] rounded px-2 py-1.5 bg-white"
                          data-testid="corporate-allowed-domains"
                        />
                        <p className="text-[10px] text-[#A3A6A3] mt-1">
                          Laissez vide pour accepter tout email (moins sûr).
                          Seuls les emails de ces domaines pourront demander un lien magique.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Phase 101cw (Feb 2026) — TVA + Tranches CA + Prix par produit */}
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#F0E4C9]">
                    <div>
                      <Label className="mb-1.5 block text-xs">Taux TVA facturation (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.1"
                        value={form.corporate_tva_rate ?? 2.6}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm({ ...form, corporate_tva_rate: v === "" ? 0 : Number(v) });
                        }}
                        placeholder="2.6"
                        data-testid="corporate-tva-rate"
                      />
                      <p className="text-[10px] text-[#A3A6A3] mt-1">Alimentation Suisse = 2.6%.</p>
                    </div>
                    <div className="flex items-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
                        onClick={() => setForm({
                          ...form,
                          corporate_tva_rate: 2.6,
                          corporate_participation_tiers: [
                            { ca_min: 0, ca_max: 500, participation_chf: 900 },
                            { ca_min: 501, ca_max: 1000, participation_chf: 700 },
                            { ca_min: 1001, ca_max: 1500, participation_chf: 500 },
                            { ca_min: 1501, ca_max: 2200, participation_chf: 250 },
                            { ca_min: 2600, ca_max: null, participation_chf: 0 },
                          ],
                        })}
                        data-testid="corporate-load-novelis-model"
                        title="Pré-remplit les tranches selon la convention Novelis (5 tranches + TVA 2.6%)"
                      >
                        Charger modèle Novelis
                      </Button>
                    </div>
                  </div>

                  <div className="pt-2">
                    <Label className="mb-2 block text-xs font-semibold">
                      Tranches de participation par CA mensuel
                    </Label>
                    <p className="text-[10px] text-[#A3A6A3] mb-2">
                      Bornes CHF incluses. Laissez « max » vide pour « et au-delà ». Les tranches remplacent le CA garanti simple.
                    </p>
                    <div className="space-y-1">
                      {(form.corporate_participation_tiers || []).map((t, idx) => (
                        <div key={idx} className="flex items-center gap-1 text-xs" data-testid={`corporate-tier-${idx}`}>
                          <span className="text-[#575A57]">De</span>
                          <Input
                            type="number" min="0" step="1"
                            value={t.ca_min ?? 0}
                            onChange={(e) => {
                              const arr = [...form.corporate_participation_tiers];
                              arr[idx] = { ...arr[idx], ca_min: Number(e.target.value) || 0 };
                              setForm({ ...form, corporate_participation_tiers: arr });
                            }}
                            className="h-8 w-24"
                          />
                          <span className="text-[#575A57]">à</span>
                          <Input
                            type="number" min="0" step="1"
                            value={t.ca_max ?? ""}
                            onChange={(e) => {
                              const arr = [...form.corporate_participation_tiers];
                              arr[idx] = { ...arr[idx], ca_max: e.target.value === "" ? null : Number(e.target.value) };
                              setForm({ ...form, corporate_participation_tiers: arr });
                            }}
                            placeholder="∞"
                            className="h-8 w-24"
                          />
                          <span className="text-[#575A57]">CHF → participation</span>
                          <Input
                            type="number" min="0" step="10"
                            value={t.participation_chf ?? 0}
                            onChange={(e) => {
                              const arr = [...form.corporate_participation_tiers];
                              arr[idx] = { ...arr[idx], participation_chf: Number(e.target.value) || 0 };
                              setForm({ ...form, corporate_participation_tiers: arr });
                            }}
                            className="h-8 w-24"
                          />
                          <span className="text-[#575A57]">CHF</span>
                          <Button
                            type="button" variant="ghost" size="sm"
                            className="h-7 px-2 text-[#C05C47] hover:bg-[#F9EAEA]"
                            onClick={() => {
                              const arr = [...form.corporate_participation_tiers];
                              arr.splice(idx, 1);
                              setForm({ ...form, corporate_participation_tiers: arr });
                            }}
                            data-testid={`corporate-tier-delete-${idx}`}
                          >
                            ✕
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button" variant="outline" size="sm"
                        className="h-7 mt-1 text-xs"
                        onClick={() => setForm({
                          ...form,
                          corporate_participation_tiers: [
                            ...(form.corporate_participation_tiers || []),
                            { ca_min: 0, ca_max: null, participation_chf: 0 },
                          ],
                        })}
                        data-testid="corporate-tier-add"
                      >
                        + Ajouter une tranche
                      </Button>
                    </div>
                  </div>

                  <div className="pt-2">
                    <div className="flex items-baseline justify-between mb-2 gap-2 flex-wrap">
                      <Label className="text-xs font-semibold">
                        Prix Novelis par produit
                        <span className="ml-2 text-[10px] text-[#A3A6A3] font-normal">
                          (laissez vide = remise globale {form.employee_discount_percent || 0}%)
                        </span>
                      </Label>
                      {(() => {
                        const overrides = form.employee_price_overrides || {};
                        const total = products.filter((p) => !p.archived).length;
                        const defined = Object.keys(overrides).filter((pid) =>
                          products.some((p) => p.id === pid && !p.archived)
                        ).length;
                        return (
                          <span
                            className="text-[10px] text-[#575A57] font-medium"
                            data-testid="corporate-override-counter"
                          >
                            <b className="text-[#1F4E3D]">{defined}</b> / {total} définis
                          </span>
                        );
                      })()}
                    </div>
                    {categories.length > 1 && (
                      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => setPriceCategoryFilter("")}
                          className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                            priceCategoryFilter === ""
                              ? "bg-[#1F4E3D] text-white border-[#1F4E3D]"
                              : "bg-white text-[#575A57] border-[#DDD] hover:border-[#1F4E3D]"
                          }`}
                          data-testid="corporate-filter-all"
                        >
                          Toutes
                        </button>
                        {categories.map((cat) => (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => setPriceCategoryFilter(cat)}
                            className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                              priceCategoryFilter === cat
                                ? "bg-[#1F4E3D] text-white border-[#1F4E3D]"
                                : "bg-white text-[#575A57] border-[#DDD] hover:border-[#1F4E3D]"
                            }`}
                            data-testid={`corporate-filter-${cat}`}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="max-h-72 overflow-y-auto border border-[#F0E4C9] rounded">
                      <table className="w-full text-xs">
                        <thead className="bg-[#FBF4E9] sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1.5 font-medium">Produit</th>
                            <th className="text-right px-2 py-1.5 w-24 font-medium">Prix public</th>
                            <th className="text-right px-2 py-1.5 w-28 font-medium">Prix Novelis</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortByName(products)
                            .filter((p) => !p.archived)
                            .filter((p) => !priceCategoryFilter || p.category === priceCategoryFilter)
                            .map((p) => {
                            const overrides = form.employee_price_overrides || {};
                            const val = overrides[p.id];
                            return (
                              <tr key={p.id} className="border-t border-[#F0F0EB]">
                                <td className="px-2 py-1">
                                  <span className="font-medium">{p.name}</span>
                                  {p.category && (
                                    <span className="text-[9px] uppercase tracking-wider text-[#8C6B2F] ml-1.5">
                                      · {p.category}
                                    </span>
                                  )}
                                  {p.sku && <span className="text-[#A3A6A3] ml-1">({p.sku})</span>}
                                </td>
                                <td className="px-2 py-1 text-right font-mono text-[#575A57]">
                                  {Number(p.price_direct || 0).toFixed(2)}
                                </td>
                                <td className="px-2 py-1 text-right">
                                  <Input
                                    type="number" min="0" step="0.5"
                                    value={val ?? ""}
                                    placeholder="—"
                                    onChange={(e) => {
                                      const next = { ...(form.employee_price_overrides || {}) };
                                      if (e.target.value === "") {
                                        delete next[p.id];
                                      } else {
                                        next[p.id] = Number(e.target.value);
                                      }
                                      setForm({ ...form, employee_price_overrides: next });
                                    }}
                                    className="h-7 text-right font-mono"
                                    data-testid={`corporate-price-override-${p.id}`}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {form.type !== "CLIENT" && (
            <div className="flex items-start gap-2 pt-2 border-t border-[#F0F0EB]">
              <Checkbox
                id="hide-grid"
                checked={form.hide_emplacement_grid}
                onCheckedChange={(v) => setForm({ ...form, hide_emplacement_grid: !!v })}
                data-testid="hide-grid-toggle"
                className="mt-0.5"
              />
              <div>
                <Label htmlFor="hide-grid" className="cursor-pointer font-medium">
                  Masquer la grille des emplacements
                </Label>
                <p className="text-xs text-[#575A57] mt-0.5">
                  Le compteur reste affiché mais la grille de gestion 10-58 est désactivée pour ce dépôt.
                </p>
              </div>
            </div>
          )}
          {form.type !== "CLIENT" && (
            <div className="flex items-start gap-2 pt-2 border-t border-[#F0F0EB]">
              <Checkbox
                id="is-local-central"
                checked={!!form.is_local_to_central}
                onCheckedChange={(v) => setForm({ ...form, is_local_to_central: !!v })}
                data-testid="is-local-central-toggle"
                className="mt-0.5"
              />
              <div>
                <Label htmlFor="is-local-central" className="cursor-pointer font-medium">
                  Distributeur local au stock central (Conthey)
                </Label>
                <p className="text-xs text-[#575A57] mt-0.5">
                  Lors d'une tournée de réappro, les lots à DLC très courte sont prioritairement
                  routés vers ce distributeur (les distribs distants reçoivent les lots à DLC plus longue).
                </p>
              </div>
            </div>
          )}

          {/* Sélection des produits affichés sur la fiche imprimable */}
          {editing && (
            <div className="pt-3 border-t border-[#F0F0EB]">
              <Label className="font-medium flex items-center gap-2 mb-1">
                <Printer className="w-4 h-4 text-[#8C6B2F]" /> Fiche imprimable — produits affichés
              </Label>
              <p className="text-xs text-[#575A57] mb-3">
                Cochez les plats que vous voulez voir apparaître sur la fiche A4 à coller sur la machine ou à remettre aux revendeurs.
                Si rien n'est coché, le PDF utilisera automatiquement les produits ayant des lots actifs ici.
                <br/>
                <span className="text-[#5847A6]">💡 1 produit sur 2 slots ? Saisissez « 10,11 » ou « 10/11 » dans le n° slot — la fiche imprimera 2 cases distinctes.</span>
              </p>
              <div className="max-h-72 overflow-y-auto border border-[#E5E5E0] rounded p-2 flex flex-col gap-1 bg-[#FBFAF6]" data-testid="menu-product-picker">
                {products.length === 0 ? (
                  <p className="text-xs text-[#A3A6A3] py-3 text-center">Aucun produit dans le catalogue.</p>
                ) : sortByName(products).map((p) => {
                  const checked = (form.displayed_product_ids || []).includes(p.id);
                  const rail = (form.displayed_product_rails || {})[p.id] || "";
                  return (
                    <div key={p.id} className="flex items-center gap-2 hover:bg-white px-2 py-1.5 rounded text-sm">
                      <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            const cur = form.displayed_product_ids || [];
                            const railsCur = { ...(form.displayed_product_rails || {}) };
                            if (!v) delete railsCur[p.id];
                            setForm({
                              ...form,
                              displayed_product_ids: v ? [...cur, p.id] : cur.filter((id) => id !== p.id),
                              displayed_product_rails: railsCur,
                            });
                          }}
                          data-testid={`menu-product-${p.sku}`}
                        />
                        <span className="text-xs font-mono text-[#A3A6A3] w-20 flex-shrink-0">{p.sku}</span>
                        <span className="text-sm flex-1" title={p.name}>{p.name}</span>
                      </label>
                      {checked && (
                        <Input
                          value={rail}
                          onChange={(e) => {
                            const rv = e.target.value.slice(0, 15);
                            const next = { ...(form.displayed_product_rails || {}) };
                            if (rv) next[p.id] = rv;
                            else delete next[p.id];
                            setForm({ ...form, displayed_product_rails: next });
                          }}
                          placeholder="10 ou 10,11"
                          className="w-28 h-10 text-base font-mono font-bold text-[#1A1C1A] text-center bg-[#FBF4E9] border-2 border-[#8C6B2F] focus:border-[#3D2914] focus:ring-1 focus:ring-[#3D2914] placeholder:font-normal placeholder:text-[#A3A6A3] placeholder:text-xs flex-shrink-0"
                          data-testid={`menu-rail-${p.sku}`}
                          title="Plusieurs slots pour le même produit ? Séparez avec , ou / (ex: 10,11)"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="text-[10px] text-[#575A57] mt-1.5 flex justify-between">
                <span>{(form.displayed_product_ids || []).length} produit(s) sélectionné(s)</span>
                {(form.displayed_product_ids || []).length > 0 && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, displayed_product_ids: [] })}
                    className="text-[#A04E2C] hover:underline"
                    data-testid="clear-menu-products"
                  >
                    Tout désélectionner
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-4 border-t border-[#E5E5E0] bg-white">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={onSave} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-distributor-btn">
            {editing ? "Mettre à jour" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
